import './new-date-picker.scss';

import { Controller, useFormContext } from 'react-hook-form';
import { TextField, TextFieldProps, Box } from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';

import { DateFormatType } from '@converge/data';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FC } from 'react';
import { FormFieldContainer } from './form-field-container';
import { NewConvergeInputLabel } from './new-converge-input-label';
import moment from 'moment-timezone';
import { formatValue } from '../../domain/entity/utils';

type INewFormDateFieldProps = {
  name: string;
  containerWidth?: number;
  views?: Array<'day' | 'month' | 'year'>;
  minDate?: Date;
  maxDate?: Date;
  isReadonly?: boolean;
  errorMessage?: string;
  isPrimaryEndorsement?: boolean;
  isCompletedDate?: boolean;
  disableValidation?: boolean;
  dataTestId?: string;
  onDateChange?: (
    value: Date | null,
    keyboardInputValue?: string | undefined
  ) => void;
} & TextFieldProps;

export const NewDateField: FC<INewFormDateFieldProps> = ({
  containerWidth = 6,
  required,
  isReadonly,
  name,
  label,
  views = ['year', 'day'],
  minDate,
  maxDate,
  onDateChange,
  defaultValue,
  errorMessage,
  isCompletedDate = false,
  disableValidation = false,
  dataTestId,
  ...otherProps
}) => {
  const { setError, clearErrors } = useFormContext();
  const { control } = useFormContext();

  const validateDate = (value: Date | null, inputValue?: string) => {
    if (disableValidation) {
      return;
    }

    // For completed date fields, only validate if there's actual input
    // For regular date fields, use the original validation logic
    clearErrors(name);

    if (isCompletedDate) {
      // Only validate if there's actual input (either a date or keyboard input)
      if (value || (inputValue && inputValue.trim())) {
        let errorMessage = '';
        // Check if the date is valid first
        if (value && formatValue(value) === 'Invalid date') {
          errorMessage = 'Invalid date';
        }
        // Also check if there's keyboard input but no valid date
        else if (!value && inputValue && inputValue.trim()) {
          errorMessage = 'Invalid date';
        }

        if (errorMessage) {
          setError(name, {
            type: 'DateFormat',
            message: errorMessage,
          });
        }
      }
    } else {
      // Original validation logic for non-completed date fields
      if (value) {
        let errorMessage = '';

        // Check if the date is valid first
        if (formatValue(value) === 'Invalid date') {
          errorMessage = 'Invalid date';
        }

        if (errorMessage) {
          setError(name, {
            type: 'DateFormat',
            message: errorMessage,
          });
        }
      }
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required,
      }}
      render={({
        field: { value, onChange, onBlur, ...otherFields },
        formState: { errors },
      }) => (
        <>
          <FormFieldContainer md={containerWidth}>
            <NewConvergeInputLabel
              label={label}
              required={required}
              className={errors[name] ? 'error-message' : ''}
            />
            <Box
              sx={{
                '& .MuiIconButton-root:not(.Mui-disabled) .MuiSvgIcon-root': {
                  color: 'black !important',
                },
              }}
            >
              <DatePicker
                className="date-picker"
                value={value ? moment.utc(value).format('YYYY-MM-DD') : ''}
                views={views}
                {...otherFields}
                disabled={otherProps.disabled || isReadonly}
                minDate={minDate}
                maxDate={maxDate}
                components={{
                  OpenPickerIcon: DateRangeIcon,
                }}
                shouldDisableDate={(date) => {
                  const momentDate = moment(date);
                  if (minDate) {
                    const momentMinDate = moment(minDate);
                    const isBefore = momentDate.isBefore(momentMinDate, 'day');
                    console.log('Date is before minDate:', isBefore);
                    if (isBefore) {
                      return true;
                    }
                  }

                  // Disable dates after maxDate
                  if (maxDate) {
                    const momentMaxDate = moment(maxDate);
                    if (momentDate.isAfter(momentMaxDate, 'day')) {
                      return true;
                    }
                  }

                  return false;
                }}
                PaperProps={{
                  sx: {
                    '& .MuiPickersDay-root': {
                      color: 'black !important',
                      '&:hover': {
                        backgroundColor: '#e7fdfb !important',
                        color: 'black !important',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#cefcf6 !important',
                        '&:hover': {
                          backgroundColor: '#e7fdfb !important',
                        },
                        '&:focus': {
                          backgroundColor: '#cefcf6 !important',
                        },
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(0, 0, 0, 0.26) !important',
                        backgroundColor: 'transparent !important',
                        cursor: 'not-allowed !important',
                        '&:hover': {
                          backgroundColor: 'transparent !important',
                        },
                      },
                    },
                    '& .MuiPickersDay-today.Mui-selected': {
                      backgroundColor: '#cefcf6 !important',
                      '&:hover': {
                        backgroundColor: '#e7fdfb !important',
                      },
                    },
                  },
                }}
                onChange={(...params) => {
                  onChange(...params);
                  const date = params[0];
                  const inputValue = params[1];
                  validateDate(date, inputValue);
                  if (onDateChange) {
                    onDateChange(...params);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="MM/DD/YYYY"
                    sx={{
                      '& input': {
                        textTransform: 'uppercase',
                        '&::placeholder': {
                          textTransform: 'uppercase',
                        },
                      },
                      '& .MuiOutlinedInput-root': {
                        textTransform: 'uppercase',
                        '& fieldset': {
                          borderColor: '#000000',
                        },
                        '&:hover fieldset': {
                          borderColor: '#28c0a7',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#23a892',
                        },
                      },

                      '& .MuiFormHelperText-root.Mui-error': {
                        marginLeft: '0px',
                        fontWeight: 600,
                      },
                    }}
                    name={name}
                    className="new-date-picker-text-field"
                    {...params}
                    {...otherProps}
                    inputProps={{
                      ...params.inputProps,
                      ...(dataTestId && { 'data-testid': dataTestId }),
                    }}
                    error={errors[name] ? !!errors : false}
                    helperText={
                      errors[name]
                        ? (errors[name]?.message as unknown as string)
                        : ''
                    }
                    {...(isCompletedDate && {
                      onKeyDown: (event) => {
                        // Only apply input restriction for completed date fields
                        // Allow: backspace, delete, tab, escape, enter, arrows, and numeric keys
                        if (
                          [8, 9, 27, 13, 46, 37, 39].indexOf(event.keyCode) !==
                            -1 ||
                          // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                          (event.keyCode === 65 && event.ctrlKey === true) ||
                          (event.keyCode === 67 && event.ctrlKey === true) ||
                          (event.keyCode === 86 && event.ctrlKey === true) ||
                          (event.keyCode === 88 && event.ctrlKey === true) ||
                          // Allow: home, end, left, right, down, up
                          (event.keyCode >= 35 && event.keyCode <= 40) ||
                          // Allow: numeric keypad (0-9)
                          (event.keyCode >= 96 && event.keyCode <= 105) ||
                          // Allow: numeric keys (0-9)
                          (event.keyCode >= 48 && event.keyCode <= 57) ||
                          // Allow: forward slash (/)
                          event.keyCode === 191 ||
                          // Allow: hyphen/dash (-)
                          event.keyCode === 189
                        ) {
                          return; // let it happen, don't do anything
                        }
                        // Ensure that it is a number and stop the keypress
                        if (
                          (event.shiftKey ||
                            event.keyCode < 48 ||
                            event.keyCode > 57) &&
                          (event.keyCode < 96 || event.keyCode > 105) &&
                          event.keyCode !== 191 &&
                          event.keyCode !== 189
                        ) {
                          event.preventDefault();
                        }
                      },
                    })}
                  />
                )}
                inputFormat={DateFormatType.USADateFormat}
                disableMaskedInput
              />
            </Box>
          </FormFieldContainer>
        </>
      )}
    />
  );
};
